import { useState, useEffect } from "react";
import shortid from "shortid";
import {
  IconButton,
  Button,
  Tooltip,
  ButtonGroup,
  Avatar,
} from "../../components";
import {
  Card,
  CardContent,
  Typography,
  TablePagination,
  Box,
  Collapse,
  Chip,
  alpha,
} from "@mui/material";

import { Pesquisar, FiltroAvancadoDialog } from "./components";
import { useImportContext } from "../../contexts/import.context";
import { filtrosUrl } from "../../helpers";
import { format, isDate } from "date-fns";

const filtros = {
  orderBy: "updatedAt",
  order: "desc",
  page: "1",
};

const StatusChip = ({ cor, titulo }) => (
  <Chip
    size="small"
    icon={
      <i
        className="ph-fill ph-circle"
        style={{ color: cor, fontSize: 10, marginRight: -2 }}
      />
    }
    label={titulo}
    sx={{
      height: 22,
      fontSize: "0.72rem",
      backgroundColor: alpha(cor, 0.1),
      color: "text.primary",
      fontWeight: 500,
      "& .MuiChip-label": { px: 0.75 },
      "& .MuiChip-icon": { ml: 0.75 },
    }}
  />
);

const RowCard = ({
  row,
  colunas,
  quantidadeCamposVisiveis = 5,
  props,
  initialCollapses,
}) => {
  const [expandido, setExpandido] = useState(false);
  const colunaAcoes = colunas.find((col) => col.field === "acoes");
  const colunaStatus = colunas.find((col) => typeof col.status === "function");
  const colunaAvatar = colunas.find((col) => typeof col.avatar === "function");

  const statusResolvido = colunaStatus ? colunaStatus.status(row) : null;
  const avatarResolvido = colunaAvatar ? colunaAvatar.avatar(row) : null;

  const colunasData = colunas.filter(
    (col) =>
      col.field !== "acoes" && col !== colunaStatus && col !== colunaAvatar,
  );

  const colunasVisiveis = colunasData.slice(0, quantidadeCamposVisiveis);
  const colunasOcultas = colunasData.slice(quantidadeCamposVisiveis);
  const temMais = colunasOcultas.length > 0;

  const handleDoubleClick = () => {
    if (props.editShow && props.onClickEdit) {
      initialCollapses();
      props.onClickEdit(row.id);
    }
  };

  const renderCampo = (col) => {
    const params = {
      row,
      value: row[col.field],
      id: row.id,
      field: col.field,
    };

    let resolvedValue = col.valueGetter
      ? col.valueGetter(params)
      : params.value;

    if (isDate(resolvedValue)) {
      resolvedValue = format(resolvedValue, "dd/MM/yyyy");
    }

    const cellContent = col.renderCell
      ? col.renderCell({ ...params, value: resolvedValue })
      : resolvedValue;

    return (
      <Box
        key={col.id}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          px: 0.5,
          py: 0.5,
        }}
      >
        <Typography
          color="text.secondary"
          sx={{
            fontWeight: 600,
            whiteSpace: "nowrap",
            minWidth: 80,
            fontSize: "0.7rem",
          }}
        >
          {col.headerName}
        </Typography>
        <Typography
          sx={{
            wordBreak: "break-word",
            textAlign: "right",
            flex: 1,
            fontSize: "0.7rem",
            fontWeight: 500,
          }}
        >
          {cellContent !== undefined && cellContent !== null ? cellContent : ""}
        </Typography>
      </Box>
    );
  };

  const temAcoes =
    colunaAcoes ||
    (!props.disableAcoes &&
      (props.imposto ||
        props.visualizarShow ||
        props.editShow ||
        props.deleteShow));

  return (
    <Card
      variant="outlined"
      onDoubleClick={handleDoubleClick}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        boxShadow: "0px 2px 6px rgba(0,0,0,0.04)",
        border: "1px solid",
        borderColor: "divider",
        cursor: props.editShow ? "pointer" : "default",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
        ...(statusResolvido?.cor && {
          borderLeft: `4px solid ${statusResolvido.cor}`,
        }),
        "&:hover": {
          backgroundColor: props.editShow ? "action.hover" : "inherit",
        },
      }}
    >
      <CardContent sx={{ pb: "12px !important", pt: 1.5, px: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1.5,
            minHeight: 32,
          }}
        >
          <Box>
            {statusResolvido && (
              <StatusChip
                cor={statusResolvido.cor ?? "#ccc"}
                titulo={statusResolvido.titulo}
              />
            )}
            {avatarResolvido && (
              <Avatar
                id={avatarResolvido.id}
                userList={avatarResolvido.userList}
              />
            )}
          </Box>

          {temAcoes && (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                ml: 1,
              }}
            >
              {colunaAcoes?.renderCell?.({
                row,
                value: row[colunaAcoes.field],
                id: row.id,
                field: colunaAcoes.field,
              })}

              {!colunaAcoes && !props.disableAcoes && (
                <>
                  {props.imposto && (
                    <Tooltip title="Visualizar Imposto" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onClickViewImposto(row.id);
                        }}
                      >
                        <i
                          className="ph-fill ph-eye"
                          style={{ fontSize: 18 }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  {props.visualizarShow && !props.editShow && (
                    <Tooltip title="Visualizar" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          initialCollapses();
                          props.onClickEdit(row.id);
                        }}
                      >
                        <i
                          className="ph-fill ph-eye"
                          style={{ fontSize: 18 }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  {props.editShow && (
                    <Tooltip title="Editar" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          initialCollapses();
                          props.onClickEdit(row.id);
                        }}
                      >
                        <i
                          className="ph-fill ph-pencil"
                          style={{ fontSize: 18 }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  {props.deleteShow && (
                    <Tooltip title="Excluir" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onClickDelete(row.id);
                        }}
                      >
                        <i
                          className="ph-fill ph-trash"
                          style={{ fontSize: 18 }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>

        {colunasVisiveis.map(renderCampo)}

        {temMais && (
          <Collapse in={expandido}>{colunasOcultas.map(renderCampo)}</Collapse>
        )}

        {temMais && (
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
                setExpandido((v) => !v);
              }}
              sx={{ fontSize: "0.7rem", color: "text.secondary", py: 0.5 }}
            >
              {expandido ? (
                <>
                  Ver menos{" "}
                  <i
                    className="ph-bold ph-caret-up"
                    style={{ marginLeft: 4 }}
                  />
                </>
              ) : (
                <>
                  Ver mais{" "}
                  <i
                    className="ph-bold ph-caret-down"
                    style={{ marginLeft: 4 }}
                  />
                </>
              )}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const DataGridCustom = (props) => {
  const { initialCollapses } = useImportContext();
  const [tipoFiltro, setTipoFiltro] = useState("simples");
  const [page, setPage] = useState(0);
  const [busca, setbusca] = useState("");
  const [queryBusca, setQueryBusca] = useState({});
  const [colunas, setColunas] = useState([]);
  const [colunaFiltroSimples, setColunaFiltroSimples] = useState(null);
  const [openFiltroAvancadoDialog, setOpenFiltroAvancadoDialog] =
    useState(false);
  const [filtrosAvancados, setFiltrosAvancados] = useState(
    props?.filtros || {},
  );

  useEffect(() => {
    atualizaColunas(props.columns);
  }, [props.columns]);

  const atualizaColunas = (columns) => {
    const colunasFiltradas = columns
      .map((column) => {
        if (column.filtrar) setColunaFiltroSimples(column);
        return { ...column, id: shortid.generate() };
      })
      .filter((column) => !column?.hide);
    setColunas(colunasFiltradas);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    if (props.paginationMode === "server") {
      filtros.page = (newPage + 1).toString();
      const retorno = { tipoFiltro };
      retorno.filtros =
        tipoFiltro === "simples"
          ? { ...filtros, ...queryBusca }
          : { ...filtros, ...filtrosAvancados };
      props.sendServer(retorno);
    }
  };

  const handlePesquisaAvancada = async () => {
    setPage(0);
    setOpenFiltroAvancadoDialog(false);
    setTipoFiltro("avancada");
    filtros.page = "1";
    props.sendServer({
      tipoFiltro: "avancada",
      filtros: { ...filtros, ...filtrosAvancados },
    });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
        }}
      >
        {props.paginationMode === "server" && !props.noFilter && (
          <>
            <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
              {!props.FiltroCustom ? (
                <Pesquisar
                  setPage={setPage}
                  filtros={filtros}
                  setQueryBusca={setQueryBusca}
                  colunas={props.columns}
                  sendServer={props.sendServer}
                  busca={busca}
                  setbusca={setbusca}
                  setTipoFiltro={setTipoFiltro}
                  tipoFiltro={tipoFiltro}
                  colunaFiltroSimples={colunaFiltroSimples}
                />
              ) : (
                props.FiltroCustom
              )}
            </Box>
            {!props.hideFiltroAvancado && (
              <Box sx={{ flexShrink: 0 }}>
                <ButtonGroup
                  variant="outlined"
                  color="inherit"
                  sx={{ height: 52, mt: +!!props.FiltroCustom }}
                >
                  <Button
                    color="inherit"
                    onClick={() => setOpenFiltroAvancadoDialog(true)}
                    size="large"
                  >
                    <span className="text-capitalize">Filtro</span>
                    <i
                      className="ph-bold ph-funnel-simple"
                      style={{
                        color: "#494C62",
                        fontSize: 18,
                        paddingLeft: 10,
                      }}
                    />
                  </Button>
                  {new URLSearchParams(window.location.search).size > 0 && (
                    <Tooltip title="Remover filtros" arrow placement="top">
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => {
                          filtrosUrl.delete();
                          props.sendServer({
                            ...filtros,
                            tipoFiltro: "simples",
                          });
                        }}
                      >
                        <i
                          className="ph-fill ph-trash"
                          style={{ color: "#494C62", fontSize: 15 }}
                        />
                      </Button>
                    </Tooltip>
                  )}
                </ButtonGroup>
              </Box>
            )}
          </>
        )}
        {props.extraActions}
      </Box>

      <Box sx={{ width: "100%" }}>
        {props.rows?.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            py={4}
          >
            Nenhum registro encontrado
          </Typography>
        )}
        {props.rows?.map((row) => (
          <RowCard
            key={row.id || shortid.generate()}
            row={row}
            colunas={colunas}
            quantidadeCamposVisiveis={props?.quantidadeCamposVisiveis}
            props={props}
            initialCollapses={initialCollapses}
          />
        ))}
      </Box>

      <TablePagination
        component="div"
        count={props.rowCount || props.rows?.length || 0}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={props.pageSize || 25}
        rowsPerPageOptions={[props.pageSize || 25]}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
        sx={{
          ".MuiTablePagination-toolbar": {
            flexWrap: "wrap",
            justifyContent: "center",
          },
          ".MuiTablePagination-spacer": { display: "none" },
        }}
      />

      {!props.noFilter && (
        <FiltroAvancadoDialog
          openFiltroAvancadoDialog={openFiltroAvancadoDialog}
          setOpenFiltroAvancadoDialog={setOpenFiltroAvancadoDialog}
          filtrosAvancados={filtrosAvancados}
          setFiltrosAvancados={setFiltrosAvancados}
          handlePesquisaAvancada={handlePesquisaAvancada}
          FormFiltroAvancado={props.FormFiltroAvancado}
        />
      )}
    </>
  );
};

export default DataGridCustom;
